/**
 * API Client for OpenLabel Dashboard Backend
 * Handles all communication with the Rails API
 */

const API_BASE_URL = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api/v1`
  : 'http://localhost:3001/api/v1'
const API_KEY = process.env.REACT_APP_API_KEY || ''

interface FetchOptions extends RequestInit {
  params?: Record<string, string | string[]>
}

/**
 * Base fetch wrapper with API key authentication
 */
async function apiFetch<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
  const { params, ...fetchOptions } = options
  
  // Build URL with query parameters
  let url = `${API_BASE_URL}${endpoint}`
  if (params) {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        searchParams.append(key, value.join(','))
      } else {
        searchParams.append(key, value)
      }
    })
    url += `?${searchParams.toString()}`
  }
  
  // Add API key header
  const headers = {
    'Content-Type': 'application/json',
    ...(API_KEY && { 'X-API-Key': API_KEY }),
    ...fetchOptions.headers,
  }
  
  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  })
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `API Error: ${response.status}`)
  }
  
  return response.json()
}

// ============================================================================
// FORMULAS API
// ============================================================================

export interface Formula {
  id: number
  product_name: string
  product_code: string
  calories_per_serving: number
  prepared_size_oz: string
}

export interface FormulaNutrient {
  value: number
  unit: string
  category: string
}

export interface FormulaWithNutrients extends Formula {
  nutrients: Record<string, FormulaNutrient>
}

/**
 * Get all formulas
 */
export async function getFormulas(): Promise<Formula[]> {
  return apiFetch<Formula[]>('/formulas')
}

/**
 * Get formula by ID with all nutrients
 */
export async function getFormula(id: number): Promise<FormulaWithNutrients> {
  return apiFetch<FormulaWithNutrients>(`/formulas/${id}`)
}

/**
 * Get all nutrients for a formula
 */
export async function getFormulaNutrients(id: number): Promise<Record<string, FormulaNutrient>> {
  return apiFetch<Record<string, FormulaNutrient>>(`/formulas/${id}/nutrients`)
}

/**
 * Get specific nutrient value for a formula
 */
export async function getFormulaNutrient(
  id: number,
  nutrientName: string
): Promise<FormulaNutrient & { nutrient_name: string }> {
  return apiFetch(`/formulas/${id}/nutrient/${encodeURIComponent(nutrientName)}`)
}

// ============================================================================
// ANALYTES API
// ============================================================================

export interface AnalyteDescription {
  name: string
  friendly_name: string
  category: string
  description: string
  health_effects: string
  sources: string | null
}

/**
 * Get all analyte descriptions
 */
export async function getAnalytes(category?: string): Promise<AnalyteDescription[]> {
  return apiFetch<AnalyteDescription[]>('/analytes', {
    params: category ? { category } : undefined,
  })
}

/**
 * Get analyte description by name
 */
export async function getAnalyte(name: string): Promise<AnalyteDescription> {
  return apiFetch<AnalyteDescription>(`/analytes/${encodeURIComponent(name)}`)
}

/**
 * Get all analyte categories
 */
export async function getAnalyteCategories(): Promise<string[]> {
  const response = await apiFetch<{ categories: string[] }>('/analytes/categories')
  return response.categories
}

// ============================================================================
// ANALYTICS API (Protected Business Logic)
// ============================================================================

export interface HeatmapCell {
  product: string
  analyte: string
  concentration: number | null
  unit: string
  status: string
  loq: number | null
  lod: number | null
  normalized: boolean
  per_100_cal: boolean
  color_intensity: number
}

export interface HeatmapData {
  cells: HeatmapCell[]
  products: string[]
  analytes: Record<string, string[]>
  metadata: {
    total_results: number
    guideline: string
    generated_at: string
  }
}

/**
 * Get heatmap data with normalized values
 */
export async function getHeatmapData(
  products: string[],
  guideline: string = 'default'
): Promise<HeatmapData> {
  return apiFetch<HeatmapData>('/analytics/heatmap_data', {
    params: {
      products,
      guideline,
    },
  })
}

export interface NormalizedResult {
  product_variant: string
  analyte: string
  concentration: number | null
  unit: string
  normalized: boolean
  per_100_cal: boolean
  [key: string]: any
}

/**
 * Get normalized test results
 */
export async function getNormalizedResults(products: string[]): Promise<{
  results: NormalizedResult[]
  count: number
}> {
  return apiFetch('/analytics/normalized_results', {
    params: { products },
  })
}

export interface FormulaComparison {
  product_name: string
  product_code: string
  nutrient_name: string
  value: number | null
  unit: string
}

/**
 * Compare nutrient values across formulas
 */
export async function compareFormulas(
  productCodes: string[],
  nutrientName: string
): Promise<{ comparison: FormulaComparison[] }> {
  return apiFetch('/analytics/formula_comparison', {
    params: {
      products: productCodes,
      nutrient: nutrientName,
    },
  })
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get formula nutrient by product name and nutrient name
 * Helper function to match the old frontend API
 */
export async function getFormulaNutrientByName(
  productName: string,
  nutrientName: string
): Promise<FormulaNutrient | null> {
  try {
    const formulas = await getFormulas()
    const formula = formulas.find(f => f.product_name === productName)
    
    if (!formula) {
      console.warn(`Formula not found: ${productName}`)
      return null
    }
    
    const nutrient = await getFormulaNutrient(formula.id, nutrientName)
    return {
      value: nutrient.value,
      unit: nutrient.unit,
      category: nutrient.category,
    }
  } catch (error) {
    console.error(`Error fetching nutrient ${nutrientName} for ${productName}:`, error)
    return null
  }
}

/**
 * Get analyte metadata by name
 * Helper function to match the old frontend API
 */
export async function getAnalyteMetadata(analyteName: string): Promise<AnalyteDescription | null> {
  try {
    return await getAnalyte(analyteName)
  } catch (error) {
    console.error(`Error fetching analyte metadata for ${analyteName}:`, error)
    return null
  }
}
